import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { setOnboardingComplete, setLifetimeAccess } from '../utils/localStorage';
import axios from 'axios';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;
  
  const sessionId = searchParams.get('session_id');
  
  useEffect(() => {
    if (!sessionId) {
      return;
    }
    
    const pollPaymentStatus = async () => {
      if (attempts >= maxAttempts) {
        setStatus('failed');
        return;
      }
      
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/payments/status/${sessionId}`
        );
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          // Mark user as having lifetime access
          setLifetimeAccess();
          setOnboardingComplete();
        } else if (response.data.status === 'expired') {
          setStatus('failed');
        } else {
          // Continue polling
          setAttempts(prev => prev + 1);
          setTimeout(pollPaymentStatus, 2000);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        setAttempts(prev => prev + 1);
        if (attempts + 1 >= maxAttempts) {
          setStatus('failed');
        } else {
          setTimeout(pollPaymentStatus, 2000);
        }
      }
    };
    
    pollPaymentStatus();
  }, [sessionId, attempts]);
  
  // Handle missing session ID
  useEffect(() => {
    if (!sessionId) {
      const timer = setTimeout(() => setStatus('failed'), 100);
      return () => clearTimeout(timer);
    }
  }, [sessionId]);
  
  const handleContinue = () => {
    navigate('/dashboard');
  };
  
  const handleRetry = () => {
    navigate('/');
  };
  
  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-paper paper-texture">
      <Card className="max-w-md w-full p-8 bg-paper-light shadow-lifted-lg border border-stone-200 text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-brown mx-auto mb-6 animate-spin" />
            <h1 className="font-serif text-2xl text-ink-900 mb-4">
              Processing your payment...
            </h1>
            <p className="text-ink-500 font-accent italic">
              Please wait while we confirm your purchase.
            </p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-sage mx-auto mb-6" />
            <h1 className="font-serif text-2xl text-ink-900 mb-4">
              Welcome to the family!
            </h1>
            <p className="text-ink-700 font-accent italic mb-6">
              Your lifetime access has been activated. Thank you for supporting The Calm Keeper Compass.
            </p>
            <Button
              onClick={handleContinue}
              className="w-full vintage-btn btn-brown rounded-lg py-6 text-lg"
            >
              Start Your Journey
            </Button>
          </>
        )}
        
        {status === 'failed' && (
          <>
            <XCircle className="w-16 h-16 text-survival-text mx-auto mb-6" />
            <h1 className="font-serif text-2xl text-ink-900 mb-4">
              Payment could not be verified
            </h1>
            <p className="text-ink-500 font-accent italic mb-6">
              Something went wrong. Please try again or contact support.
            </p>
            <Button
              onClick={handleRetry}
              className="w-full vintage-btn btn-brown rounded-lg py-6 text-lg"
            >
              Try Again
            </Button>
          </>
        )}
      </Card>
    </div>
  );
};

export default PaymentSuccess;
